import type {ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import React from 'react';
import {TextSize, Text} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {BlockCategoryMap} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import {EmailMakerBlock} from '@Components/email-maker-block-manager/components/email-maker-block';
import {getUniqueString} from '@Utils/string.util';
import {EmailComponentTemplates} from '@Components/email-maker-block-manager/components/email-component-templates/email-component-templates';
import {getBlocksFromCategories, getIconFromBlockId} from '@Libraries/email-component-template-library';
import type {Block, Editor} from 'grapesjs';
import styles from './image-block-panel.module.scss';
import {ElementsPanelHeader} from '@Components/email-maker-block-manager/components/elements-panel-header';
import {useIsMobileScreen} from '@Components/email-maker-block-manager/email-maker-block-manager-grapesjs-editor-context';
import {noop} from '@Utils/general.util';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';

interface ImageBlockPanelProps {
  blocksMap: BlockCategoryMap;
  headerLabel?: string;
  showBackButton?: boolean;
  onBackButton?: VoidFunction;
  gjsEditor: Editor;
}

const IMAGE_TEMPLATE_CATEGORY_ID = 2;
const GA4_TRACKING_NAME = 'Image';

export function ImageBlockPanel({blocksMap, gjsEditor, headerLabel = '', showBackButton = false, onBackButton = noop}: ImageBlockPanelProps): ReactElement<any> {
  const BLOCK_CATEGORIES = [window.PMW.emails.blocks.CATEGORY_IMAGE, window.PMW.emails.blocks.CATEGORY_MY_DESIGNS];
  const imageBlocks = getBlocksFromCategories(blocksMap, BLOCK_CATEGORIES);
  const [showShadow, setShowShadow] = React.useState<boolean>(false);
  const isMobileScreen = useIsMobileScreen();
  const panelContentRef = useRef<HTMLDivElement>(null);

  const getBlockVisual = (block: Block): ReactElement<any> => {
    return (
      <>
        <Icon icon={getIconFromBlockId(block.getId())} className={styles.icon} size={IconSize.SIZE_ICON_16} />
        <Text val={block.getLabel()} size={TextSize.XSMALL} />
      </>
    );
  };

  const isMyDesignsBlock = (block: Block): boolean => {
    return block.getId() === window.PMW.emails.blocks.ID_DESIGN;
  };

  const onBlockClick = (block: Block): void => {
    if (isMyDesignsBlock(block)) {
      gjsEditor.runCommand(window.PMW.emails.commands.OPEN_DESIGN_SELECTOR, {isMultiSelectAllowed: true});
    }
  };

  const getDefaultImageBlocks = (): ReactElement<any> => {
    return (
      <div className={styles.blocksGrid}>
        {imageBlocks.map((block) => {
          const shouldNotAutoAppend = isMyDesignsBlock(block);

          return (
            <EmailMakerBlock
              block={block}
              extraClasses={styles.block}
              key={getUniqueString()}
              onAddToCanvas={trackGA4EventOnDefaultImageAdd}
              shouldAppendBlockOnClick={!shouldNotAutoAppend}
              onClick={onBlockClick.bind(null, block)}
            >
              {getBlockVisual(block)}
            </EmailMakerBlock>
          );
        })}
      </div>
    );
  };

  const showPanelHeaderShadowIfNeeded = (): void => {
    setShowShadow((panelContentRef.current?.scrollTop ?? 0) > 0);
  };

  const trackGA4EventOnDefaultImageAdd = (): void => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_ADD_ELEMENT, {[GA4EventParamName.TYPE]: GA4_TRACKING_NAME});
  };

  useEffect(() => {
    panelContentRef.current?.addEventListener('scroll', showPanelHeaderShadowIfNeeded, {passive: true});

    return (): void => {
      panelContentRef.current?.removeEventListener('scroll', showPanelHeaderShadowIfNeeded);
    };
  }, []);

  return (
    <div ref={panelContentRef} className={`${styles.panelContent} ${isMobileScreen ? styles.mobile : ''}`}>
      <ElementsPanelHeader headerLabel={headerLabel} showBackButton={showBackButton} onBackButton={onBackButton} showShadow={showShadow} />
      {getDefaultImageBlocks()}
      <EmailComponentTemplates headerLabelOverride={window.i18next.t('pmwjs_templates')} selectedCategoryId={IMAGE_TEMPLATE_CATEGORY_ID} />
    </div>
  );
}
